import { useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import ReactTooltip from 'react-tooltip';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { AssetContext } from '@pages/Asset/Asset.query';
import ProductImagePng from '@assets/png/product.png';
import { RouterContext } from '@app/routes/routerContext';
import { TagsMenu } from '@components/SideBarMenus/TagsMenu/TagsMenu';
import { PhotosMenu } from '@components/SideBarMenus/PhotosMenu/PhotosMenu';
import { PinnedDataMenu } from '@components/SideBarMenus/PinnedDataMenu/PinnedDataMenu';
import { Group } from '@mantine/core';
import { TagNum } from '@components/TagNum/TagNum';
import { getProductValue } from '@utils/getProductValue';
import { ITooltip } from '@components/Tooltip';
import { ManualAdd } from '@pages/Products/components';
import { AssetMatchLabel } from '@components/index';
import { useSelector } from 'react-redux';
import { checkDevice } from '@store/redux/features/mobile/mobileSelectors';
import { BActionIcon } from '@components/BActionIcon/BActionIcon';
import { useElementSize } from '@mantine/hooks';
import { Status, PinnedData, Tags, Photos, InternalData } from './components';

import {
  AssetSidebarContainer,
  Title,
  TitleContainer,
  Head,
  Product,
  ProductImage,
  ProductImg,
  ProductName,
  Temporary,
  TemporaryText,
  TemporaryImg,
  Divider,
  Line,
  LineText,
  Wrapper,
  SidebarHeading,
  TooltipDiv,
  SidebarGroup,
} from './Sidebar.styles';

import { SidebarLoading } from './Sidebar.loading';

import { SideBarMenu } from './components/SideBarMenu/SideBarMenu';
import Shortcuts from './components/Shortcuts/Shortcuts';
import { AddPhotoButton } from './components/AddPhotoButton';
import { IdentificationStatusTooltipBody } from './IdentificationStatusTooltipBody';
import { NoFeatureMenu } from '../Updates/Components/NoFeatureMenu/NoFeatureMenu';

const SIDEBAR_HEIGHT_APPROXIMATION = 167;

export const Sidebar = () => {
  const history = useHistory();

  const [show, setShow] = useState(false);
  const [openManualEdit, setOpenManualEdit] = useState(false);
  const { equipmentQuery, picturesQuery, shortcutRefs, sidebarHeadingHeight } =
    useContext(AssetContext);
  const { from } = useContext(RouterContext);
  const { isDesktop, isMobile } = useSelector(checkDevice());

  const { height: _sidebarHeadingHeight, ref: sidebarHeadingRef } =
    useElementSize<HTMLDivElement>();

  useEffect(() => {
    sidebarHeadingHeight.set(
      _sidebarHeadingHeight || SIDEBAR_HEIGHT_APPROXIMATION,
    );
    return () => {};
  }, [_sidebarHeadingHeight]);

  const equipment = equipmentQuery?.data;

  if (!equipment) {
    return <SidebarLoading />;
  }

  const { product } = equipment;
  const { name, model, manufacturerName } = getProductValue(equipment);

  const getProductName = () => {
    if (name) {
      return name;
    }

    if (manufacturerName && model) {
      return `${manufacturerName} ${model}`;
    }

    if (manufacturerName) {
      return `${manufacturerName} Asset`;
    }

    return 'Unknown Asset';
  };
  const returnPath = () => {
    if (from.includes('/updates')) return from;
    return '/products';
  };
  const returnTo = () => {
    if (from.includes('/updates')) {
      return 'Updates';
    }

    return 'Products';
  };

  const handleChange = (isOverflowed: boolean) => {
    setShow(isOverflowed);
  };

  const ItemName = () => {
    return (
      <>
        <ProductName
          onChange={handleChange}
          limitOfLines={2}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            lineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {getProductName()}
        </ProductName>
        <ReactTooltip
          className="tooltip"
          id="nameToolTip"
          effect="solid"
          disable={!show}
        />
      </>
    );
  };

  const numOTags = equipment?.tags.length ?? '';
  const numOPics = picturesQuery?.data?.length ?? '';

  const identificationStatusTooltipId = equipment.id;
  return (
    <AssetSidebarContainer
      data-offsettop={_sidebarHeadingHeight || SIDEBAR_HEIGHT_APPROXIMATION}
    >
      <SidebarHeading ref={sidebarHeadingRef}>
        <Head>
          <TitleContainer onClick={() => history.push(returnPath())}>
            <BActionIcon icon="arrow-left" variant="transparent" />
            {isDesktop && <Title>Return to {returnTo()}</Title>}
          </TitleContainer>
          <Wrapper>
            <TooltipDiv
              data-for={identificationStatusTooltipId}
              data-tip
              clickable={equipment.identificationStatus === 'unidentified'}
              onClick={() => {
                if (equipment.identificationStatus === 'unidentified') {
                  setOpenManualEdit(true);
                }
              }}
            >
              <AssetMatchLabel status={equipment.identificationStatus} />
            </TooltipDiv>
            <SideBarMenu
              isUnidentified={equipment.identificationStatus === 'unidentified'}
              setOpenManualEdit={setOpenManualEdit}
            />
            <ITooltip id={identificationStatusTooltipId}>
              <IdentificationStatusTooltipBody
                identificationStatus={equipment.identificationStatus}
              />
            </ITooltip>
          </Wrapper>
        </Head>
        <Product>
          <ProductImage>
            {product?.imageUrl ? (
              <ProductImg src={product?.imageUrl} />
            ) : (
              <ProductImg src={ProductImagePng} />
            )}
          </ProductImage>
          {ItemName()}
        </Product>
        {product?.tempProductId && (
          <Temporary>
            <TemporaryImg>
              <BlueIcon icon="warning-sign" color="#ff9900" />
            </TemporaryImg>
            <TemporaryText>Temporary Product</TemporaryText>
          </Temporary>
        )}
        <Status />
        <Divider />
      </SidebarHeading>
      <div>
        {isMobile && (
          <Shortcuts className="reset-global" shortcutRefs={shortcutRefs} />
        )}

        <SidebarGroup>
          <Line>
            <LineText>Pinned Data</LineText>
            {isDesktop && <PinnedDataMenu />}
            {isMobile && <NoFeatureMenu />}
          </Line>
          <PinnedData />
        </SidebarGroup>

        <SidebarGroup>
          <Line>
            <Group spacing={8}>
              <LineText>Tags</LineText>
              <TagNum>{numOTags}</TagNum>
            </Group>
            <TagsMenu />
          </Line>
          <Tags />
        </SidebarGroup>

        <SidebarGroup className="last">
          <Line>
            <Group spacing={8}>
              <LineText>Photos</LineText>
              <TagNum>{numOPics}</TagNum>
            </Group>
            {isMobile && <AddPhotoButton />}
            {isDesktop && <PhotosMenu />}
          </Line>
          <Photos />
        </SidebarGroup>

        <InternalData />

        {openManualEdit && (
          <ManualAdd
            open={openManualEdit}
            onClose={() => setOpenManualEdit(false)}
            oldAsset={equipment}
          />
        )}
      </div>
    </AssetSidebarContainer>
  );
};
